import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import confetti from 'canvas-confetti';
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { withTenant } from '../services/data';
import { colors, fonts, radius } from './theme';

/* ─── Brand colors for confetti (inlined to avoid module-level TDZ risk) ─── */
const BRAND_COLORS = ['#EE8172', '#EFDD84', '#FFFFFF', '#F6D9D4', '#5C50A1'];
const GOLD = '#D4A843';

/* ─── Reduced motion check ─── */
function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/* ─── Last animation tracking ─── */
function getLastAnimation() {
  try { return localStorage.getItem('reve_last_celebration') || ''; } catch { return ''; }
}
function setLastAnimation(type) {
  try { localStorage.setItem('reve_last_celebration', type); } catch { /* */ }
}

/* ─── Animation Components ─── */

function ConfettiAnimation() {
  useEffect(() => {
    const burst = (opts) => confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors: BRAND_COLORS, ...opts });
    burst({ angle: 60, origin: { x: 0, y: 0.65 } });
    setTimeout(() => burst({ angle: 120, origin: { x: 1, y: 0.65 } }), 200);
    setTimeout(() => burst({ origin: { x: 0.5, y: 0.4 } }), 400);
  }, []);
  return null; // confetti renders on its own canvas
}

function EmojiAnimation({ emoji, keyframes, duration = '3s' }) {
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 80, zIndex: 9999, pointerEvents: 'none', overflow: 'hidden' }}>
      <style>{keyframes}</style>
      <div style={{ position: 'absolute', fontSize: 64, animation: `celebrationMove ${duration} ease-in-out forwards` }}>{emoji}</div>
    </div>
  );
}

function UnicornAnimation() {
  const kf = `@keyframes celebrationMove { 0% { left: -80px; top: 40%; opacity: 1; } 50% { top: 25%; } 100% { left: 110%; top: 35%; opacity: 0; } }`;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 80, zIndex: 9999, pointerEvents: 'none', overflow: 'hidden' }}>
      <style>{kf}</style>
      <div style={{ position: 'absolute', fontSize: 72, animation: 'celebrationMove 3s ease-in-out forwards', filter: 'drop-shadow(0 0 8px rgba(238,129,114,0.5))' }}>🦄</div>
      {[...Array(8)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute', fontSize: 16, animation: `celebrationMove ${2.5 + i * 0.15}s ease-in-out forwards`,
          top: `${30 + Math.random() * 20}%`, opacity: 0.8,
        }}>✨</div>
      ))}
    </div>
  );
}

function DancingBearAnimation() {
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 80, zIndex: 9999, pointerEvents: 'none', overflow: 'hidden' }}>
      <style>{`
        @keyframes bearWalk {
          0% { left: -140px; }
          25% { left: 30%; }
          30% { left: 32%; transform: scaleX(1); }
          35% { left: 32%; transform: scaleX(1) translateY(-20px); }
          40% { left: 32%; transform: scaleX(1) translateY(0) rotate(10deg); }
          45% { left: 32%; transform: scaleX(1) translateY(-15px) rotate(-10deg); }
          50% { left: 34%; transform: scaleX(1) translateY(0) rotate(360deg); }
          55% { left: 36%; transform: scaleX(1) translateY(-20px); }
          60% { left: 38%; transform: scaleX(1) translateY(0) rotate(720deg); }
          65% { left: 40%; transform: scaleX(1); }
          100% { left: 110%; transform: scaleX(1); }
        }
        @keyframes bearBounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes noteFloat { 0% { opacity: 1; transform: translateY(0) rotate(0deg); } 100% { opacity: 0; transform: translateY(-60px) rotate(20deg); } }
      `}</style>
      {/* Bear body — SVG cartoon bear */}
      <div style={{ position: 'absolute', bottom: '12%', animation: 'bearWalk 4s ease-in-out forwards' }}>
        <svg width="120" height="120" viewBox="0 0 120 120" style={{ animation: 'bearBounce 0.4s ease-in-out infinite' }}>
          {/* Ears */}
          <circle cx="30" cy="20" r="16" fill="#8B5E3C" />
          <circle cx="90" cy="20" r="16" fill="#8B5E3C" />
          <circle cx="30" cy="20" r="9" fill="#C4956A" />
          <circle cx="90" cy="20" r="9" fill="#C4956A" />
          {/* Head */}
          <circle cx="60" cy="42" r="30" fill="#A0724A" />
          {/* Muzzle */}
          <ellipse cx="60" cy="52" rx="16" ry="12" fill="#C4956A" />
          {/* Eyes */}
          <circle cx="48" cy="38" r="4" fill="#2C1810" />
          <circle cx="72" cy="38" r="4" fill="#2C1810" />
          <circle cx="49" cy="37" r="1.5" fill="#fff" />
          <circle cx="73" cy="37" r="1.5" fill="#fff" />
          {/* Nose */}
          <ellipse cx="60" cy="48" rx="5" ry="3.5" fill="#2C1810" />
          {/* Mouth */}
          <path d="M54 54 Q60 60 66 54" fill="none" stroke="#2C1810" strokeWidth="1.5" strokeLinecap="round" />
          {/* Body */}
          <ellipse cx="60" cy="85" rx="28" ry="24" fill="#A0724A" />
          <ellipse cx="60" cy="88" rx="18" ry="14" fill="#C4956A" />
          {/* Arms */}
          <ellipse cx="28" cy="78" rx="10" ry="16" fill="#8B5E3C" transform="rotate(-15 28 78)" />
          <ellipse cx="92" cy="78" rx="10" ry="16" fill="#8B5E3C" transform="rotate(15 92 78)" />
          {/* Feet */}
          <ellipse cx="42" cy="108" rx="12" ry="8" fill="#8B5E3C" />
          <ellipse cx="78" cy="108" rx="12" ry="8" fill="#8B5E3C" />
        </svg>
        {/* Music notes */}
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{
            position: 'absolute', top: -20 - i * 8, left: 30 + i * 20, fontSize: 20,
            animation: `noteFloat 1.2s ease-out ${0.8 + i * 0.5}s forwards`, opacity: 0,
          }}>{i % 2 === 0 ? '🎵' : '🎶'}</div>
        ))}
      </div>
    </div>
  );
}

function NinjaAnimation() {
  const kf = `@keyframes celebrationMove { 0% { left: -80px; bottom: 10%; top: auto; } 50% { bottom: 15%; top: auto; } 100% { left: 110%; bottom: 8%; top: auto; } }`;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 80, zIndex: 9999, pointerEvents: 'none', overflow: 'hidden' }}>
      <style>{kf}</style>
      <div style={{ position: 'absolute', fontSize: 56, animation: 'celebrationMove 2.5s ease-in-out forwards' }}>🥷</div>
    </div>
  );
}

function GoldStarsAnimation() {
  const stars = React.useMemo(() => [...Array(25)].map((_, i) => ({
    left: 2 + Math.random() * 96,
    size: [16, 16, 24, 24, 24, 32][Math.floor(Math.random() * 6)],
    duration: 1.8 + Math.random() * 1.5,
    delay: Math.random() * 1.2,
    rotate: Math.random() * 360,
  })), []);
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 80, zIndex: 9999, pointerEvents: 'none', overflow: 'hidden' }}>
      <style>{`
        @keyframes starFall { 0% { top: -40px; opacity: 1; transform: rotate(0deg) scale(0.5); } 20% { opacity: 1; transform: rotate(90deg) scale(1); } 100% { top: 100vh; opacity: 0; transform: rotate(360deg) scale(0.8); } }
        @keyframes twinkle { 0%,100% { filter: brightness(1); } 50% { filter: brightness(1.6) drop-shadow(0 0 6px #D4A843); } }
      `}</style>
      {stars.map((s, i) => (
        <div key={i} style={{
          position: 'absolute', fontSize: s.size, left: `${s.left}%`, color: GOLD,
          animation: `starFall ${s.duration}s ease-in ${s.delay}s forwards, twinkle ${0.6 + Math.random() * 0.4}s ease-in-out ${s.delay}s infinite`,
        }}>⭐</div>
      ))}
    </div>
  );
}

function BalloonsAnimation() {
  const BALLOON_COLORS = [colors.coral, GOLD, colors.white, colors.pearl, '#F5A898', '#A8D8EA', colors.purple];
  const balloons = React.useMemo(() => [...Array(14)].map((_, i) => ({
    left: 5 + Math.random() * 90,
    color: BALLOON_COLORS[Math.floor(Math.random() * BALLOON_COLORS.length)],
    size: 28 + Math.random() * 16,
    duration: 2.8 + Math.random() * 1.5,
    delay: Math.random() * 0.8,
    drift: -15 + Math.random() * 30,
  })), []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 80, zIndex: 9999, pointerEvents: 'none', overflow: 'hidden' }}>
      <style>{`
        @keyframes balloonFloat {
          0% { bottom: -80px; opacity: 0; }
          5% { opacity: 1; }
          85% { opacity: 1; }
          100% { bottom: 110vh; opacity: 0; }
        }
        @keyframes wobble { 0%,100% { transform: translateX(0) rotate(-2deg); } 25% { transform: translateX(8px) rotate(2deg); } 75% { transform: translateX(-8px) rotate(-3deg); } }
      `}</style>
      {balloons.map((b, i) => (
        <div key={i} style={{
          position: 'absolute', left: `${b.left}%`,
          animation: `balloonFloat ${b.duration}s ease-out ${b.delay}s forwards`,
        }}>
          <div style={{ animation: `wobble ${1.5 + Math.random() * 0.5}s ease-in-out infinite`, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Balloon SVG */}
            <svg width={b.size} height={b.size * 1.3} viewBox="0 0 40 52">
              <ellipse cx="20" cy="18" rx="17" ry="18" fill={b.color} opacity="0.9" />
              <ellipse cx="20" cy="18" rx="17" ry="18" fill="url(#shine)" opacity="0.3" />
              <polygon points="15,34 20,40 25,34" fill={b.color} opacity="0.8" />
              {/* String */}
              <path d="M20 40 Q22 46 19 52" fill="none" stroke="#999" strokeWidth="0.8" />
              <defs><radialGradient id="shine" cx="35%" cy="30%"><stop offset="0%" stopColor="#fff" /><stop offset="100%" stopColor="transparent" /></radialGradient></defs>
            </svg>
          </div>
        </div>
      ))}
    </div>
  );
}

function RainbowAnimation() {
  const RAINBOW = ['#FF0000', '#FF7F00', '#FFFF00', '#00FF00', '#0000FF', '#4B0082', '#8B00FF'];
  const raindrops = React.useMemo(() => [...Array(40)].map(() => ({
    left: Math.random() * 100, delay: Math.random() * 1.5, duration: 0.6 + Math.random() * 0.4,
  })), []);
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 80, zIndex: 9999, pointerEvents: 'none', overflow: 'hidden' }}>
      <style>{`
        @keyframes rainDrop { 0% { top: -10px; opacity: 0.4; } 100% { top: 100vh; opacity: 0; } }
        @keyframes rainbowFadeIn { 0% { opacity: 0; } 25% { opacity: 0.6; } 75% { opacity: 0.6; } 100% { opacity: 0; } }
        @keyframes rainFadeOut { 0% { opacity: 1; } 40% { opacity: 1; } 60% { opacity: 0; } 100% { opacity: 0; } }
      `}</style>
      {/* Rain */}
      <div style={{ animation: 'rainFadeOut 4s ease-in-out forwards' }}>
        {raindrops.map((r, i) => (
          <div key={i} style={{
            position: 'absolute', left: `${r.left}%`, width: 1.5, height: 12, borderRadius: 1,
            background: 'rgba(180,190,200,0.5)',
            animation: `rainDrop ${r.duration}s linear ${r.delay}s infinite`,
          }} />
        ))}
      </div>
      {/* Rainbow arc — 7 concentric arcs */}
      <div style={{ position: 'absolute', top: '-20%', left: '-10%', right: '-10%', animation: 'rainbowFadeIn 4s ease-in-out forwards' }}>
        <svg viewBox="0 0 1000 500" style={{ width: '120%', maxHeight: '60vh' }}>
          {RAINBOW.map((color, i) => (
            <path
              key={i}
              d={`M ${50 + i * 18} 480 A ${450 - i * 18} ${350 - i * 14} 0 0 1 ${950 - i * 18} 480`}
              fill="none"
              stroke={color}
              strokeWidth={14}
              opacity={0.45}
              strokeLinecap="round"
            />
          ))}
          {/* Soft glow layer */}
          {RAINBOW.map((color, i) => (
            <path
              key={`g${i}`}
              d={`M ${50 + i * 18} 480 A ${450 - i * 18} ${350 - i * 14} 0 0 1 ${950 - i * 18} 480`}
              fill="none"
              stroke={color}
              strokeWidth={28}
              opacity={0.08}
              strokeLinecap="round"
              filter="blur(8px)"
            />
          ))}
        </svg>
      </div>
    </div>
  );
}

function RocketAnimation() {
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 80, zIndex: 9999, pointerEvents: 'none', overflow: 'hidden' }}>
      <style>{`@keyframes rocketLaunch { 0% { bottom: -60px; left: 50%; transform: translateX(-50%); opacity: 1; } 100% { bottom: 110vh; left: 48%; transform: translateX(-50%) rotate(-10deg); opacity: 0; } }`}</style>
      <div style={{ position: 'absolute', fontSize: 64, animation: 'rocketLaunch 2.5s ease-in forwards' }}>🚀</div>
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute', fontSize: 20, left: `${46 + Math.random() * 8}%`,
          animation: `rocketLaunch ${3 + i * 0.3}s ease-in ${0.2 + i * 0.1}s forwards`, opacity: 0.6,
        }}>{['💨', '✨', '⭐'][i % 3]}</div>
      ))}
    </div>
  );
}

function FireworksAnimation() {
  useEffect(() => {
    const duration = 3500;
    const end = Date.now() + duration;
    const interval = setInterval(() => {
      if (Date.now() > end) { clearInterval(interval); return; }
      confetti({
        particleCount: 40, startVelocity: 30, spread: 360, ticks: 60,
        origin: { x: Math.random(), y: Math.random() * 0.5 },
        colors: [...BRAND_COLORS, GOLD, '#FF6B6B', '#4ECDC4'],
      });
    }, 250);
    return () => clearInterval(interval);
  }, []);
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, pointerEvents: 'none' }}>
      <style>{`@keyframes fireworkGlow { 0%,100% { opacity: 0; } 50% { opacity: 0.15; } }`}</style>
      <div style={{ width: '100%', height: '100%', background: `radial-gradient(circle, ${colors.coral}22 0%, transparent 70%)`, animation: 'fireworkGlow 1s ease-in-out infinite' }} />
    </div>
  );
}

function FoxAnimation() {
  const kf = `@keyframes celebrationMove { 0% { left: -80px; top: 50%; transform: rotate(0deg); } 25% { transform: rotate(360deg); } 50% { top: 45%; transform: rotate(720deg); } 75% { transform: rotate(1080deg); } 100% { left: 110%; top: 50%; transform: rotate(1440deg); } }`;
  return <EmojiAnimation emoji="🦊" keyframes={kf} duration="3s" />;
}

/* ─── Animation Registry ─── */
const ANIMATIONS = {
  confetti: ConfettiAnimation,
  unicorn: UnicornAnimation,
  dancingBear: DancingBearAnimation,
  ninja: NinjaAnimation,
  goldStars: GoldStarsAnimation,
  balloons: BalloonsAnimation,
  rainbow: RainbowAnimation,
  rocket: RocketAnimation,
  fireworks: FireworksAnimation,
  fox: FoxAnimation,
};

const NON_FIREWORK_TYPES = Object.keys(ANIMATIONS).filter((k) => k !== 'fireworks');

function pickRandom(exclude) {
  const available = NON_FIREWORK_TYPES.filter((t) => t !== exclude);
  return available[Math.floor(Math.random() * available.length)];
}

/* ─── Message Overlay ─── */
function CelebrationOverlay({ type, message, onDismiss }) {
  const [visible, setVisible] = useState(true);
  const AnimComponent = ANIMATIONS[type];
  const reduced = prefersReducedMotion();

  useEffect(() => {
    const timer = setTimeout(() => { setVisible(false); onDismiss(); }, type === 'fireworks' ? 4000 : 3500);
    return () => clearTimeout(timer);
  }, [type, onDismiss]);

  if (!visible) return null;

  if (reduced) {
    // Subtle toast for reduced motion
    return (
      <div onClick={onDismiss} style={{
        position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
        background: colors.noir, color: colors.white, padding: '12px 24px', borderRadius: radius.md,
        fontSize: 15, fontWeight: 500, fontFamily: fonts.body, boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
        cursor: 'pointer', maxWidth: '90vw', textAlign: 'center',
      }}>
        {message}
      </div>
    );
  }

  return (
    <div onClick={onDismiss} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998, cursor: 'pointer' }}>
      {AnimComponent ? <AnimComponent /> : null}
      {message ? (
        <div style={{
          position: 'fixed', bottom: 100, left: '50%', transform: 'translateX(-50%)', zIndex: 10000,
          background: 'rgba(15,16,17,0.85)', color: colors.white, padding: '14px 28px', borderRadius: radius.lg,
          fontSize: 17, fontWeight: 600, fontFamily: fonts.heading, boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          textAlign: 'center', maxWidth: '85vw', backdropFilter: 'blur(8px)',
          animation: 'fadeInUp 0.4s ease-out',
        }}>
          <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateX(-50%) translateY(20px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>
          {message}
        </div>
      ) : null}
    </div>
  );
}

/* ─── Milestone Definitions ─── */
const MILESTONES = {
  first_post: { animation: 'confetti', message: 'Welcome to the Rêve feed! 🎉' },
  fifth_post: { animation: 'dancingBear', message: "You're on a roll! 🐻" },
  tenth_post: { animation: 'unicorn', message: 'Posting legend! 🦄' },
  first_shoutout: { animation: 'goldStars', message: 'Spreading the love! ⭐' },
  third_shoutout: { animation: 'unicorn', message: "You're a culture builder! 🦄" },
  first_listing: { animation: 'balloons', message: 'Your first Sneak Peek is live! 🎈' },
  first_buyer_match: { animation: 'confetti', message: "You've got a match! 🏡" },
  first_vendor: { animation: 'fox', message: 'Building the community! 🦊' },
  first_flow: { animation: 'ninja', message: 'FLOW activated! 🥷' },
  flow_50: { animation: 'confetti', message: 'Halfway to FLOW mastery!' },
  flow_75: { animation: 'unicorn', message: "You're crushing it! 🦄" },
  flow_100: { animation: 'fireworks', message: 'FLOW MASTER! 🥷🔥' },
  flow_perfect_streak_3: { animation: 'rainbow', message: 'Ninja Legend Status! 🌈' },
  first_dm: { animation: 'balloons', message: 'Connection made! 💬' },
  first_document: { animation: 'rocket', message: 'Knowledge shared! 🚀' },
};

/* ─── Context ─── */
const CelebrationContext = createContext(null);

export function CelebrationProvider({ children, user }) {
  const [active, setActive] = useState(null); // { type, message }

  const triggerCelebration = useCallback((type, message, { force } = {}) => {
    if (!type || !ANIMATIONS[type]) return;

    let finalType = type;
    if (!force) {
      // Don't repeat same animation consecutively (unless forced for testing)
      const last = getLastAnimation();
      if (type !== 'fireworks' && type === last) {
        finalType = pickRandom(last);
      }
    }
    setLastAnimation(finalType);

    setActive({ type: finalType, message });
  }, []);

  const dismiss = useCallback(() => { setActive(null); }, []);

  // Check and trigger milestone. Resolves true only when the milestone was
  // recorded just now (RW-2264: Feed.js uses `first_post` as the one earned
  // moment to ask for push permission), false when already achieved or on error.
  const checkMilestone = useCallback(async (milestoneId) => {
    if (!user?.uid || !MILESTONES[milestoneId]) return false;

    try {
      const ref = doc(db, 'users', user.uid, 'milestones', milestoneId);
      const snap = await getDoc(ref);
      if (snap.exists()) return false; // Already achieved

      // Record milestone
      await setDoc(ref, withTenant(`users/${user.uid}/milestones`, { achievedAt: serverTimestamp(), milestoneId }));

      // Trigger animation
      const m = MILESTONES[milestoneId];
      triggerCelebration(m.animation, m.message);
      return true;
    } catch { return false; }
  }, [user, triggerCelebration]);

  // FLOW submit celebration (random non-firework for score > 60, fireworks for 100)
  const celebrateFlowSubmit = useCallback((score) => {
    if (score >= 100) {
      triggerCelebration('fireworks', 'FLOW MASTER! 🥷🔥');
    } else if (score > 60) {
      const last = getLastAnimation();
      triggerCelebration(pickRandom(last), `Great week! ${score}/100 🥷`);
    }
    // score <= 60: no animation (toast handled by FlowScorecard)
  }, [triggerCelebration]);

  // Memoized so a celebration showing and clearing (`active`) doesn't
  // re-render every useCelebration() consumer — Feed among them.
  const value = useMemo(
    () => ({ triggerCelebration, checkMilestone, celebrateFlowSubmit }),
    [triggerCelebration, checkMilestone, celebrateFlowSubmit],
  );

  return (
    <CelebrationContext.Provider value={value}>
      {children}
      {active ? <CelebrationOverlay type={active.type} message={active.message} onDismiss={dismiss} /> : null}
    </CelebrationContext.Provider>
  );
}

/* ─── Hook ─── */
export function useCelebration() {
  const ctx = useContext(CelebrationContext);
  if (!ctx) {
    // Fallback for components outside provider
    return { triggerCelebration: () => {}, checkMilestone: () => {}, celebrateFlowSubmit: () => {} };
  }
  return ctx;
}

export { MILESTONES, ANIMATIONS };
